import { AlertTriangle, Mail } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { STATUSES, STATUS_COLOR } from "./shared";

export default function OverviewTab(props) {
  const { PRODUCTS, analytics, rangeMode, revenue, sendDigest, setRangeMode, stock, tab } = props;
  return (
          <div className="space-y-px" data-testid="overview-tab">
            <div className="flex justify-end mb-4">
              <button onClick={sendDigest} data-testid="send-digest-btn"
                className="flex items-center gap-2 border border-zinc-700 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                <Mail size={13} /> Email me a digest
              </button>
            </div>
            {analytics?.low_stock?.length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/5 p-5 mb-6 flex items-start gap-3" data-testid="low-stock-alert">
                <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-1">Low Stock ({analytics.low_stock.length})</p>
                  <p className="text-sm text-[#A1A1AA]">
                    {analytics.low_stock.map((l) => {
                      const prod = PRODUCTS.find((p) => p.id === l.product_id);
                      return `${prod ? prod.name : l.product_id} (${l.quantity} left)`;
                    }).join(" · ")}
                  </p>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-px mb-6">
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
                    Revenue · {rangeMode === "all" ? "All Time" : rangeMode === "30" ? "Last 30 Days" : "Last 7 Days"}
                  </p>
                  <div className="flex gap-1">
                    {["7", "30", "all"].map((m) => (
                      <button key={m} onClick={() => setRangeMode(m)} data-testid={`range-${m}`}
                        className={`font-mono text-[9px] uppercase tracking-wide px-2 py-1 border transition-colors ${rangeMode === m ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-700 text-[#71717A] hover:border-[#D4AF37]/50"}`}>
                        {m === "all" ? "All" : `${m}D`}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="font-serif text-4xl mb-4" data-testid="revenue-range">
                  ${rangeMode === "all" ? (analytics?.total_revenue ?? 0) : (analytics?.revenue_range ?? 0)}
                </p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={analytics?.daily || []}>
                    <CartesianGrid stroke="#1c1c1c" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "monospace" }} axisLine={{ stroke: "#27272A" }} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
                    <Tooltip contentStyle={{ background: "#0C0C0C", border: "1px solid #27272A", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }} labelStyle={{ color: "#D4AF37" }} />
                    <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} dot={{ r: 2, fill: "#D4AF37" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-4">Top Sellers · Units</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={analytics?.top_sellers || []} layout="vertical" margin={{ left: 10 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 10 }} width={120} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0C0C0C", border: "1px solid #27272A", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }} labelStyle={{ color: "#D4AF37" }} cursor={{ fill: "#141414" }} />
                    <Bar dataKey="qty" fill="#D4AF37" barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px">
              {STATUSES.map((s) => (
                <div key={s} className="border border-zinc-800 bg-[#0C0C0C] p-5" data-testid={`status-count-${s}`}>
                  <p className={`font-serif text-4xl ${STATUS_COLOR[s]}`}>{analytics?.status_counts?.[s] ?? 0}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">{s}</p>
                </div>
              ))}
            </div>
          </div>
  );
}
